/* rekeyState — give sandbox-seeded records real, collision-proof ids.
   The sandbox seeds deterministic ids ("cust-demo-1", "q-demo-1",
   "p-demo-1", "wo-p-demo-1") and hub tokens ("demo-seed-hub-1"). Those are
   fine inside one throwaway sandbox, but customers / quotes / projects /
   work_orders share GLOBAL primary keys across every workspace and hub
   tokens are globally unique — so the second contractor who "keeps" their
   sandbox collides with the first one's rows, RLS refuses the upsert, the
   customers table never syncs and every Sign & pay / hub link they mint is
   dead ("Link not found"). This re-keys those records and rewrites every
   cross-reference; the app runs it on promote and once, as a repair, on a
   real workspace that still carries seeded ids. */

import { newId } from "./ids";

const SEED_ID = /(^|-)demo(-|$)/i;
const SEED_TOKEN = /^demo-/i;

function isSeedId(id: any): boolean { return typeof id === "string" && SEED_ID.test(id); }

/* does any synced record still carry a sandbox id / token? */
function hasSeedIds(state: any): boolean {
  const s = state || {};
  const any = (rows: any[], f: (r: any) => boolean) => Array.isArray(rows) && rows.some((r) => r && f(r));
  return any(s.customers, (c) => isSeedId(c.id) || (c.hubToken && SEED_TOKEN.test(String(c.hubToken))))
    || any(s.quotes, (q) => isSeedId(q.id))
    || any(s.projects, (p) => isSeedId(p.id))
    || any(s.workOrders, (w) => isSeedId(w.id));
}

/* returns { state, changed } — `changed` is how many ids were replaced.
   `all: true` re-keys every record (used on promote, where nothing outside
   the browser references the ids yet); otherwise only seeded ids move. */
function rekeyState(state: any, opts?: { all?: boolean }): { state: any; changed: number } {
  const s = state || {};
  const all = !!(opts && opts.all);
  const mapOf = (rows: any[], prefix: string) => {
    const m: Record<string, string> = {};
    (Array.isArray(rows) ? rows : []).forEach((r) => { if (r && typeof r.id === "string" && (all || isSeedId(r.id)) && !m[r.id]) m[r.id] = newId(prefix); });
    return m;
  };
  const cust = mapOf(s.customers, "cust-");
  const quo = mapOf(s.quotes, "q");
  const proj = mapOf(s.projects, "p");
  const wo = mapOf(s.workOrders, "wo-");
  const re = (m: Record<string, string>, id: any) => (typeof id === "string" && m[id]) || id;
  let changed = Object.keys(cust).length + Object.keys(quo).length + Object.keys(proj).length + Object.keys(wo).length;

  const customers = (Array.isArray(s.customers) ? s.customers : []).map((c: any) => {
    if (!c) return c;
    const n = { ...c, id: re(cust, c.id) };
    if (n.hubToken && SEED_TOKEN.test(String(n.hubToken))) { delete n.hubToken; changed++; }   // the app mints a real one on demand
    return n;
  });
  const quotes = (Array.isArray(s.quotes) ? s.quotes : []).map((q: any) => q && ({ ...q, id: re(quo, q.id), customerId: re(cust, q.customerId), projectId: re(proj, q.projectId) }));
  const projects = (Array.isArray(s.projects) ? s.projects : []).map((p: any) => p && ({ ...p, id: re(proj, p.id), fromQuote: re(quo, p.fromQuote), customerId: re(cust, p.customerId) }));
  const workOrders = (Array.isArray(s.workOrders) ? s.workOrders : []).map((w: any) => w && ({ ...w, id: re(wo, w.id), projectId: re(proj, w.projectId), quoteId: re(quo, w.quoteId), customerId: re(cust, w.customerId) }));

  // local message threads are keyed by customer id
  let threads = s.threads;
  if (threads && typeof threads === "object") {
    const t: any = {};
    Object.keys(threads).forEach((k) => { t[re(cust, k)] = threads[k]; });
    threads = t;
  }
  // settings that point at records: purchase orders → work order, referral payouts → customer/quote
  let settings = s.settings;
  if (settings && typeof settings === "object") {
    settings = { ...settings };
    if (Array.isArray(settings.purchaseOrders)) settings.purchaseOrders = settings.purchaseOrders.map((po: any) => po && ({ ...po, workOrderId: re(wo, po.workOrderId), quoteId: re(quo, po.quoteId) }));
    if (Array.isArray(settings.referralPayouts)) settings.referralPayouts = settings.referralPayouts.map((rp: any) => rp && ({ ...rp, customerId: re(cust, rp.customerId), quoteId: re(quo, rp.quoteId) }));
  }
  const out: any = { ...s, customers, quotes, projects, workOrders };
  if (threads !== undefined) out.threads = threads;
  if (settings !== undefined) out.settings = settings;
  return { state: out, changed };
}

export { rekeyState, hasSeedIds, isSeedId };
